import { LoadingErrorPage, NotFoundErrorPage } from '@bizzabo/design-system';
import {
    useAnalytics,
    useEvent,
    useTickets,
    useTrackUserGetToRingFrom,
    useCurrentUser,
} from '@bizzabo/entities';
import { useFeatureToggle } from '@bizzabo/feature-toggle-client';

import useAuth from './auth';
import useFullStory from './hooks/useFullStory';
import useGainsightExperience from './hooks/useGainsightExperience';
import useInternalJS from './hooks/useInternalJS';
import { ChatProvider } from '@bizzabo/chat-core';
import { SocketProvider } from './contexts/Socket';
import { useEffect, useState } from 'react';
import useZendesk from './hooks/useZendesk';
import { AdaChatContext } from './contexts/AdaChatContext';
import { useAda } from './hooks/useAda';
import useGetFeedback from './hooks/useGetFeedback';
import EmbedPreventThirdPartyTrackingMessage from './embed/EmbedPreventThirdPartyTrackingMessage';

type Props = {
    eventUniqueName: string;
    embedMode?: boolean;
};

const Main: React.FC<Props> = ({ eventUniqueName, embedMode = false, children }) => {
    const isAuth = useAuth(eventUniqueName, embedMode);
    const event = useEvent(eventUniqueName);
    const [hasStorageAccess, setHasStorageAccess] = useState<null | boolean>(null);
    const user = useCurrentUser(event?.data?.id, { enabled: isAuth });
    const tickets = useTickets(event?.data?.id, { enabled: isAuth });
    const isEventNotFound = event?.error?.response?.status === 404;
    const shouldShowMessageForEmbeddedChat =
        embedMode && !hasStorageAccess && hasStorageAccess !== null && !isAuth;
    useFullStory(user.data);
    useGainsightExperience(user.data, event.data);
    useGetFeedback(user.data, event.data, embedMode);
    useInternalJS(eventUniqueName, embedMode);

    const isSupportChatEnabled = useAda(user.data, event.data, isAuth);
    useZendesk(event.data, embedMode);

    useAnalytics({
        event: event.data,
        user: user.data,
        tickets: tickets.data,
        isEmbedMode: embedMode,
    });
    useTrackUserGetToRingFrom();

    const isJoinChannelAsync = useFeatureToggle('join-channel-async');

    useEffect(() => {
        if (embedMode) {
            parent?.postMessage('av_iframe_loaded', '*');
            const checkStorageAccess = () => {
                document?.hasStorageAccess?.()?.then?.(hasAccess => {
                    setHasStorageAccess(!!hasAccess);
                });
            };

            checkStorageAccess();
        }
    }, []);

    if (isEventNotFound) {
        return <NotFoundErrorPage />;
    }

    if (shouldShowMessageForEmbeddedChat) {
        return <EmbedPreventThirdPartyTrackingMessage />;
    }

    if (event?.error?.response?.status) {
        return <LoadingErrorPage />;
    }

    if (!isAuth || event.isLoading || user.isLoading || tickets.isLoading) {
        return null;
    }

    return (
        <ChatProvider
            eventId={event.data?.id}
            user={user.data}
            accountId={event.data?.accountId}
            isJoinChannelAsync={isJoinChannelAsync}
        >
            <AdaChatContext isChatEnabled={isSupportChatEnabled}>
                <SocketProvider event={event.data}>{children}</SocketProvider>
            </AdaChatContext>
        </ChatProvider>
    );
};

export default Main;
